import {
  SupplierRegistrationConfiguration,
  SupplierRegistrationCustomerReferences,
} from '@supplierRegistration/domain/supplierRegistration';
import * as customerServices from '@supplierRegistration/services/supplierRegistrationCustomerDataServices';
import * as asyncProcessServices from '@supplierRegistration/services/supplierRegistrationAsyncInitiateValidationProcessServices';
import Log from '@app/libs/logger';
import type { UtmParams } from '@app/libs/analytics/events';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import Loadable, { flowad, LoadableCreator, LoadingState } from '@app/utils/Loadable';
import { HttpStatus, isRequestError, isWebErrorContent, RequestError } from '@app/libs/request';
import SupplierRegistrationThemeStore from '@supplierRegistration/stores/infraStores/SupplierRegistrationThemeStore';
import qs from 'query-string';
import {
  AsyncDataInternalLoadErrors,
  AsyncInitiateValidationMaskedData,
  toAsyncInitiateValidationMaskedData,
} from '@supplierRegistration/domain/supplierRegistrationAsyncInitiateValidationProcess';
import { isDefined } from '@app/utils/utils';
import { WebErrorCode } from '@app/libs/request/httpConsts';
import { isValueInEnum } from '@app/utils/enumUtils';
import sleep from '@app/utils/sleep';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

/**
 * Load lifecycle of the reCAPTCHA `api.js` script:
 * - `pending`  — not yet resolved (or captcha disabled).
 * - `loaded`   — script loaded and `grecaptcha` is available.
 * - `errored`  — script failed to load (network/CDN failure, or blocked before it ran).
 *
 * Populated from the widget's `asyncScriptOnLoad` callback so token acquisition and
 * telemetry can reason about *why* a token could not be produced instead of guessing.
 */
export type CaptchaScriptLoadState = 'pending' | 'loaded' | 'errored';

export default class SupplierRegistrationCustomerDataStore {
  @observable currentReference: SupplierRegistrationCustomerReferences | null | undefined = undefined;
  @observable currentConfiguration: Loadable<SupplierRegistrationConfiguration | null> = LoadableCreator.notStarted();
  @observable currentAsyncId: string | null | undefined = undefined;
  @observable currentAsyncProcessMaskedData: Loadable<AsyncInitiateValidationMaskedData> | null = LoadableCreator.notStarted();
  @observable private _utmParams: UtmParams = {};
  @observable captchaScriptLoadState: CaptchaScriptLoadState = 'pending';

  private _currentCaptchaRef: React.RefObject<ReCAPTCHA> | undefined = undefined;
  private _themeStore: SupplierRegistrationThemeStore;

  constructor(themeStore: SupplierRegistrationThemeStore) {
    this._themeStore = themeStore;
    makeObservable(this);
  }

  setUtmParams = action((params: UtmParams): void => {
    // Capture-once: UTM params typically appear only on the entry URL. Ignore a
    // later param-less navigation so it cannot wipe the originally-captured values.
    if (Object.keys(params).length === 0) {
      return;
    }
    this._utmParams = params;
  });

  get utmParams(): UtmParams {
    return this._utmParams;
  }

  setCaptchaScriptLoadState = action((state: CaptchaScriptLoadState): void => {
    this.captchaScriptLoadState = state;
  });

  getCaptchaRef = (): React.RefObject<ReCAPTCHA> => {
    if (!this._currentCaptchaRef) {
      this._currentCaptchaRef = React.createRef<ReCAPTCHA>();
    }

    return this._currentCaptchaRef;
  };

  /**
   * Executes the invisible reCAPTCHA widget and returns a fresh token, or `undefined`
   * when none could be produced after retries. The flow is fail-open: callers still
   * send the request without the token and let the backend decide. `context` labels the
   * call site so every log line can be traced to the flow that produced it.
   */
  getCaptchaToken = async (context: string): Promise<string | undefined> => {
    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 1000;
    // Short timeout when the script is unavailable — fail fast, retrying cannot help.
    // Generous timeout once loaded — slow-3G executions can take >5s but must not hang forever
    // (also caps the frozen-UI window when the user dismisses an interactive challenge).
    const BLOCKED_TIMEOUT_MS = 5000;
    const LOADED_TIMEOUT_MS = 15000;
    const timedOut = Symbol('timedOut');

    // Defensive reset before the first attempt: a widget left in a solved state — e.g. a
    // previous submit that failed *after* its token was issued — would otherwise resolve
    // executeAsync with that stale, already-consumed token, which the backend rejects as a
    // duplicate, trapping the user in a retry loop until it expires.
    this.getCaptchaRef()?.current?.reset();

    let lastFailureReason = 'unknown';

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      const captchaRef = this.getCaptchaRef()?.current;
      const scriptLoaded = this.captchaScriptLoadState === 'loaded' || !!window['grecaptcha'];
      const baseContext = {
        context,
        attempt,
        maxAttempts: MAX_ATTEMPTS,
        scriptLoadState: this.captchaScriptLoadState,
        grecaptchaOnWindow: !!window['grecaptcha'],
        hasWidget: !!captchaRef,
      };

      try {
        const timeoutMs = scriptLoaded ? LOADED_TIMEOUT_MS : BLOCKED_TIMEOUT_MS;
        const startedAt = performance.now();
        const timeoutPromise = new Promise<typeof timedOut>((resolve) => setTimeout(() => resolve(timedOut), timeoutMs));
        const result = await Promise.race([captchaRef?.executeAsync() ?? Promise.resolve(null), timeoutPromise]);
        const elapsedMs = Math.round(performance.now() - startedAt);

        if (result === timedOut) {
          lastFailureReason = scriptLoaded ? 'timeout-after-load' : 'timeout-script-unavailable';
          Log.exception(
            new Error(
              `[reCAPTCHA][${context}] executeAsync timed out after ${timeoutMs}ms on attempt ${attempt}/${MAX_ATTEMPTS} — ${
                scriptLoaded
                  ? 'script loaded but execution never resolved (challenge dismissed or grecaptcha hung)'
                  : 'script never loaded, likely blocked by an ad blocker, CSP, or the network'
              }`,
            ),
            { ...baseContext, timeoutMs, elapsedMs, reason: lastFailureReason },
          );

          // Script never loaded → retrying cannot help, so fail fast and stop.
          // Script loaded but the execution hung (typically the user dismissed an
          // interactive challenge) → do NOT give up: fall through to the reset + retry
          // below so the challenge can be re-shown on the next attempt.
          if (!scriptLoaded) {
            break;
          }
        } else if (result) {
          Log.event('SV_RECAPTCHA_TOKEN_OBTAINED', { ...baseContext, elapsedMs, tokenLength: result.length });
          return result;
        } else {
          lastFailureReason = captchaRef ? 'empty-token' : 'widget-not-mounted';
          Log.exception(
            new Error(
              `[reCAPTCHA][${context}] ${
                captchaRef
                  ? 'executeAsync resolved without a token (challenge expired or refused)'
                  : 'captcha widget not mounted — executeAsync never ran'
              } on attempt ${attempt}/${MAX_ATTEMPTS}`,
            ),
            { ...baseContext, elapsedMs, reason: lastFailureReason },
          );
        }
      } catch (e) {
        lastFailureReason = 'execute-threw';
        Log.exception(e, {
          ...baseContext,
          reason: lastFailureReason,
          errorMessage: e instanceof Error ? e.message : String(e),
        });
      }

      if (attempt < MAX_ATTEMPTS) {
        await sleep(RETRY_DELAY_MS);
        this.getCaptchaRef()?.current?.reset();
      }
    }

    this.getCaptchaRef()?.current?.reset();

    Log.exception(
      new Error(
        `[reCAPTCHA][${context}] no token obtained (last failure: ${lastFailureReason}) — proceeding without token, backend will decide`,
      ),
      {
        context,
        reason: lastFailureReason,
        scriptLoadState: this.captchaScriptLoadState,
        grecaptchaOnWindow: !!window['grecaptcha'],
        hasWidget: !!this.getCaptchaRef()?.current,
      },
    );

    return undefined;
  };

  @computed
  get currentAsyncProcessMaskedDataLoadError(): AsyncDataInternalLoadErrors | WebErrorCode | null {
    if (this.currentAsyncProcessMaskedData?.loadState !== LoadingState.Rejected) {
      return null;
    }

    if (this.currentAsyncProcessMaskedData.stateMetadata) {
      return SupplierRegistrationCustomerDataStore.extractErrorTypeFromAsyncLoadingError(
        this.currentAsyncProcessMaskedData.stateMetadata.error,
      );
    }

    return AsyncDataInternalLoadErrors.errorLoadingAsyncMaskedData;
  }

  @computed
  get customerName(): string | null {
    if (this._themeStore.theme.brand?.customerName) {
      return this._themeStore.theme.brand?.customerName;
    }

    if (this.currentConfiguration.isResolved() && this.currentConfiguration.result?.clientName) {
      return this.currentConfiguration.result.clientName;
    }

    return null;
  }

  tryLoadCustomerData = flow(function* (
    this: SupplierRegistrationCustomerDataStore,
    referenceId: string | null,
    subdomain: string | null,
    asyncId: string | null,
    isAsyncIdValid: boolean,
  ) {
    yield this.tryLoadCustomerConfiguration(referenceId, subdomain);
    yield this.tryLoadAsyncInitiateValidationProcessData(asyncId, isAsyncIdValid);
  });

  tryLoadCustomerConfiguration = flow(function* (
    this: SupplierRegistrationCustomerDataStore,
    referenceId: string | null,
    subdomain: string | null,
  ) {
    const newReference = this.convertToCustomerReferences(referenceId, subdomain);

    if (SupplierRegistrationCustomerDataStore.isCustomerReferencesEqual(newReference, this.currentReference)) {
      return;
    }

    this.currentReference = newReference;

    if (this.currentReference === null) {
      this.currentConfiguration = LoadableCreator.resolved(null);
      return;
    }

    try {
      yield this.loadCustomerConfiguration(this.currentReference);
    } catch {}
  });

  private loadCustomerConfiguration = flowad<
    SupplierRegistrationConfiguration | null,
    [references: SupplierRegistrationCustomerReferences]
  >(
    (newValue) => {
      this.currentConfiguration = newValue;
    },
    async (references: SupplierRegistrationCustomerReferences): Promise<SupplierRegistrationConfiguration | null> => {
      try {
        let configurationServerResponse;

        if (references.subdomain !== null) {
          configurationServerResponse = await customerServices.getSupplierRegistrationConfigurationBySubdomain(
            references.subdomain,
          );
        } else {
          try {
            configurationServerResponse = await customerServices.getSupplierRegistrationConfigurationByRef(
              references.referenceId,
            );
          } catch (error: unknown) {
            if (
              isRequestError(error) &&
              isWebErrorContent(error.responseJSON) &&
              error.responseJSON.error === 'SUPPLIER_VALIDATION_SHOULD_BE_SERVED_AS_SUBDOMAIN'
            ) {
              const { newAddress: newBaseAddress } = error.responseJSON.additionalData;
              await this.redirectToSubdomain(newBaseAddress);
            } else {
              Log.exception('unknown error while fetching ref', { error });
            }

            throw error;
          }
        }

        if (configurationServerResponse?.brand) {
          this._themeStore.setThemeFromServer(configurationServerResponse.brand);
        }

        return configurationServerResponse;
      } catch (e: unknown) {
        const requestError = e as RequestError;

        // Check if the error is "configuration not found"
        if (
          requestError.code === HttpStatus.notFound &&
          isWebErrorContent(requestError.responseJSON) &&
          requestError.responseJSON.error === 'SUPPLIER_VALIDATION_CONFIGURATION_NOT_FOUND'
        ) {
          return null;
        }

        throw e;
      }
    },
  );

  tryLoadAsyncInitiateValidationProcessData = flow(function* (
    this: SupplierRegistrationCustomerDataStore,
    asyncId: string | null,
    isAsyncIdValid: boolean,
  ) {
    if (!isAsyncIdValid && !this.currentAsyncProcessMaskedData?.isRejected()) {
      this.currentAsyncProcessMaskedData = LoadableCreator.rejected(
        AsyncDataInternalLoadErrors.asyncMaskedDataInvalidQueryParamValue,
      );
      return;
    }

    if (asyncId === this.currentAsyncId) {
      return;
    }

    this.currentAsyncId = asyncId;

    if (this.currentAsyncId === null) {
      this.currentAsyncProcessMaskedData = null;
      return;
    }

    yield this.loadAsyncInitiateValidationProcessData(this.currentAsyncId);
  });

  private loadAsyncInitiateValidationProcessData = flowad<AsyncInitiateValidationMaskedData, [asyncId: string]>(
    (newValue) => {
      this.currentAsyncProcessMaskedData = newValue;
    },
    async (asyncId: string): Promise<AsyncInitiateValidationMaskedData> => {
      if (!this.currentConfiguration.isResolved() || !this.currentConfiguration.result) {
        throw AsyncDataInternalLoadErrors.errorLoadingRef;
      }

      const asyncProcessMaskedDataServerResponse = await asyncProcessServices.getSupplierRegistrationConfigurationByRef(
        this.currentConfiguration.result.ref,
        asyncId,
      );

      return toAsyncInitiateValidationMaskedData(asyncProcessMaskedDataServerResponse);
    },
  );

  private async redirectToSubdomain(newBaseAddress: string): Promise<void> {
    Log.event('SV_REF_REDIRECT_TO_BRAND_ADDRESS', { newBaseAddress });

    const parsedUrlQueryParams = qs.parseUrl(window.location.href).query;
    delete parsedUrlQueryParams.ref;

    const parsedUrlQueryParamsStringified = qs.stringify(parsedUrlQueryParams);

    const newAddress = newBaseAddress + (parsedUrlQueryParamsStringified.length ? `?${parsedUrlQueryParamsStringified}` : '');

    window.location.assign(newAddress);
    // We want to fool the webapp to keep showing the loader while the page is redirecting to the subdomain.
    // If the sleep does not happen, the web app thinks the configuration failed to load and shows an error screen
    await sleep(10000);
  }

  private convertToCustomerReferences(
    referenceId: string | null,
    subdomain: string | null,
  ): SupplierRegistrationCustomerReferences | null {
    if (subdomain) {
      return { subdomain, referenceId: null };
    }

    if (referenceId) {
      return { referenceId, subdomain: null };
    }

    return null;
  }

  private static isCustomerReferencesEqual(
    first: SupplierRegistrationCustomerReferences | null | undefined,
    second: SupplierRegistrationCustomerReferences | null | undefined,
  ): boolean {
    if (first === second) {
      return true;
    }

    if (!isDefined(first) || !isDefined(second)) {
      return false;
    }

    return first.subdomain === second.subdomain && first.referenceId === second.referenceId;
  }

  private static extractErrorTypeFromAsyncLoadingError(error: unknown): AsyncDataInternalLoadErrors | WebErrorCode {
    if (isRequestError(error) && isWebErrorContent(error.responseJSON)) {
      return error.responseJSON.error;
    }

    if (typeof error === 'string' && isValueInEnum(AsyncDataInternalLoadErrors, error)) {
      return error;
    }

    return AsyncDataInternalLoadErrors.errorLoadingAsyncMaskedData;
  }
}
